import React, { useEffect, useState } from "react";
import {
  X,
  AlertTriangle,
  Image as ImageIcon,
  CheckCircle2,
} from "lucide-react";
import {
  getSafetyObservationById,
  getUserDetailsById,
  GEtbyProjectID,
} from "../../../api";
// import { ThreadEntry } from "./SafetyChecklistHistoryModal";
import { ThreadEntry } from "./SafetyChecklistHistoryModal";
import { SignatureViewModal } from "./SafetyChecklistReadonlyModal";
import { UserRound, Eye, Signature } from "lucide-react";
import RiskMatrixModal from "./Safety_User_View/Observation_Dashboard/RiskMatrixModal";
import {
  getObservationSignOffUsers,
  hasValidSignature,
} from "./SafetyObservationSignOffSection";
import {
  buildAttemptReviewEvents,
  parseRejectionHistory,
} from "./Safety_User_View/Observation_Dashboard/observationReviewHelpers";

// ── FIX: resolveMediaUrl is now defined locally in this file instead of
// ── being imported from Makerdashboard.jsx. The imported version was
// ── not correctly resolving observation photo paths, so the image
// ── never rendered. This is the same known-good implementation already
// ── used in CheckerDashboard.jsx.
const resolveMediaUrl = (path) => {
  if (!path) return null;
  const strPath = typeof path === "string" ? path.trim() : String(path || "").trim();
  if (!strPath) return null;

  if (strPath.startsWith("http://") || strPath.startsWith("https://")) {
    if (strPath.includes("/checklists/checklists/media/")) {
      return strPath.replace("/checklists/checklists/media/", "/checklists/media/");
    }
    return strPath;
  }

  const base =
    window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost"
      ? "http://127.0.0.1:8001"
      : "https://konstruct.world/checklists";

  let clean = strPath.startsWith("/") ? strPath : "/" + strPath;
  if (clean.startsWith("/checklists/")) {
    clean = clean.replace("/checklists", "");
  }
  return base + clean;
};

const ObservationSignOffRow = ({ user, onView }) => {
  const hasSignature = Boolean(String(user?.signature || "").trim());

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-muted/20 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <UserRound className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {user.label}
          </p>
          <p className="text-sm font-bold text-foreground">
            {user.name}
            {user.designation && (
              <span className="ml-1.5 text-[11px] font-medium text-muted-foreground">
                ({user.designation})
              </span>
            )}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => onView(user)}
        disabled={!hasSignature}
        className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition ${
          hasSignature
            ? "border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
            : "cursor-not-allowed border border-border bg-muted text-muted-foreground"
        }`}
      >
        <Eye className="h-3.5 w-3.5" />
        View
      </button>
    </div>
  );
};

const ObservationSignOffSection = ({ detail, onViewSignature }) => {
  const users = getObservationSignOffUsers(detail, detail?.attempts);
  const visibleUsers = users.filter((u) => hasValidSignature(u?.signature));

  const rejectionHistory = parseRejectionHistory(detail?.rejection_history);

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm mt-6 mb-6">
      <div className="mb-4">
        <h3 className="text-sm font-bold text-foreground">
          Sign Off
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          {/* Available workflow signatures for this Observation. */}
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {visibleUsers.length > 0 ? (
          visibleUsers.map((u, i) => (
            <ObservationSignOffRow key={i} user={u} onView={onViewSignature} />
          ))
        ) : (
          <p className="text-xs italic text-muted-foreground col-span-full">
            No signatures recorded yet.
          </p>
        )}
      </div>

      {rejectionHistory.length > 0 && (
        <div className="mt-6 border-t pt-4">
          <h4 className="text-sm font-bold text-red-600 mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Rejection History ({rejectionHistory.length})
          </h4>
          <div className="space-y-3">
            {rejectionHistory.map((rej, i) => {
              const stage = String(
                rej?.checker_level ||
                  rej?.stage ||
                  "CHECKER",
              )
                .replace(/_/g, " ")
                .trim();

              const reason =
                rej?.reason ||
                rej?.remarks ||
                "No rejection reason provided.";

              const reviewedAt =
                rej?.reviewed_at ||
                rej?.timestamp ||
                null;

              return (
                <div
                  key={i}
                  className="bg-red-50 p-3 rounded-xl border border-red-100"
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-bold text-red-700">
                      - {stage}
                    </span>
                    <span className="text-[10px] text-slate-500">
                      {formatDateTime(reviewedAt)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700">
                    <span className="font-semibold">Reason:</span>{" "}
                    {reason}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  const datePart = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} · ${timePart}`;
};

const ReadonlyInfoValue = ({ label, value }) => (
  <div className="rounded-xl border border-border bg-muted/30 px-3 py-2">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </p>
    <p className="mt-1 break-words text-sm font-semibold text-foreground">
      {String(value ?? "").trim() || "—"}
    </p>
  </div>
);

const ObservationInfoCard = ({ detail }) => {
  if (!detail) return null;

  const meta = detail.report_header_meta || {};

  const fields = [
    { label: "Format No.", value: meta.format_no || "ADL-OH&S-F012" },
    { label: "Revision No.", value: meta.revision_no || "R0" },
    { label: "Issued Date", value: "1/09/2025" },
    { label: "Revision Date", value: meta.revision_date || "—" },
    { label: "Project", value: detail.project_name || "—" },
    {
      label: "Date of Observation",
      value: detail.created_at
        ? new Date(detail.created_at).toLocaleDateString("en-GB")
        : "—",
    },
    {
      label: "Observation No.",
      value: detail.observation_no ? `#${detail.observation_no}` : (detail.id ? `#${detail.id}` : "—"),
    },
    {
      label: "Report No.",
      value: detail?.report_number || "—",
    },
  ];

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm mb-6">
      <div className="mb-4">
        <h3 className="text-sm font-bold text-foreground">
          Observation Information
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          SAFETY & HOUSEKEEPING OBSERVATION REPORT
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {fields.map((field, idx) => (
          <ReadonlyInfoValue
            key={idx}
            label={field.label}
            value={field.value}
          />
        ))}
      </div>
    </div>
  );
};

const ObservationCard = ({ idx, title, children, status }) => (
  <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-xs font-bold text-white shadow-sm">
          {idx}
        </span>
        <h3 className="text-sm font-semibold leading-relaxed text-foreground sm:text-base">
          {title}
        </h3>
      </div>
      {status && (
        <span
          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${status === "completed" || status === "approved" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}
        >
          {status.toUpperCase()}
        </span>
      )}
    </div>
    <div className="pl-11">{children}</div>
  </div>
);

export default function SafetyObservationReadonlyModal({
  open,
  onClose,
  checklistId,
  pendingStatusLabel,
}) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [signatureUser, setSignatureUser] = useState(null);
  const [showRiskMatrix, setShowRiskMatrix] = useState(false);

  useEffect(() => {
    if (!open || !checklistId) {
      setDetail(null);
      return;
    }
    let mounted = true;
    const loadChecklist = async () => {
      try {
        setLoading(true);
        const res = await getSafetyObservationById(checklistId);
        const fetchedDetail = res?.data || null;

        if (fetchedDetail) {
          try {
            if (
              fetchedDetail.creator_id &&
              (!fetchedDetail.creator_name || !fetchedDetail.creator_designation)
            ) {
              const crRes = await getUserDetailsById(fetchedDetail.creator_id);
              if (crRes?.data) {
                fetchedDetail.creator_name =
                  fetchedDetail.creator_name ||
                  crRes.data.user_name ||
                  crRes.data.username ||
                  "Creator";
                fetchedDetail.creator_designation =
                  fetchedDetail.creator_designation ||
                  crRes.data.designation ||
                  crRes.data.job_title ||
                  crRes.data.role ||
                  "";
              }
            }
          } catch (e) {
            console.error("Error fetching creator details", e);
          }

          try {
            if (
              fetchedDetail.maker_id &&
              (!fetchedDetail.maker_name || !fetchedDetail.maker_designation)
            ) {
              const mRes = await getUserDetailsById(fetchedDetail.maker_id);
              if (mRes?.data) {
                fetchedDetail.maker_name =
                  fetchedDetail.maker_name ||
                  mRes.data.user_name ||
                  mRes.data.username ||
                  "Maker";
                fetchedDetail.maker_designation =
                  fetchedDetail.maker_designation ||
                  mRes.data.designation ||
                  mRes.data.job_title ||
                  mRes.data.role ||
                  "";
              }
            }
          } catch (e) {
            console.error("Error fetching maker details", e);
          }

          try {
            if (
              fetchedDetail.checker_id &&
              (!fetchedDetail.checker_name || !fetchedDetail.checker_designation)
            ) {
              const cRes = await getUserDetailsById(fetchedDetail.checker_id);
              if (cRes?.data) {
                fetchedDetail.checker_name =
                  fetchedDetail.checker_name ||
                  cRes.data.user_name ||
                  cRes.data.username ||
                  "Checker";
                fetchedDetail.checker_designation =
                  fetchedDetail.checker_designation ||
                  cRes.data.designation ||
                  cRes.data.job_title ||
                  cRes.data.role ||
                  "";
              }
            }
          } catch (e) {
            console.error("Error fetching checker details", e);
          }

          try {
            if (
              fetchedDetail.final_checker_id &&
              (!fetchedDetail.final_checker_name ||
                !fetchedDetail.final_checker_designation)
            ) {
              const fRes = await getUserDetailsById(
                fetchedDetail.final_checker_id,
              );
              if (fRes?.data) {
                fetchedDetail.final_checker_name =
                  fetchedDetail.final_checker_name ||
                  fRes.data.user_name ||
                  fRes.data.username ||
                  "Final Checker";
                fetchedDetail.final_checker_designation =
                  fetchedDetail.final_checker_designation ||
                  fRes.data.designation ||
                  fRes.data.job_title ||
                  fRes.data.role ||
                  "";
              }
            }
          } catch (e) {
            console.error("Error fetching final checker details", e);
          }

          try {
            if (
              fetchedDetail.project_id &&
              (!fetchedDetail.project_name ||
                fetchedDetail.project_name === "—")
            ) {
              const pRes = await GEtbyProjectID(fetchedDetail.project_id);
              if (pRes?.data) {
                fetchedDetail.project_name =
                  pRes.data.project_name || pRes.data.name || "";
              }
            }
          } catch (e) {
            console.error("Error fetching project details", e);
          }

          if (mounted) {
            setDetail(fetchedDetail);
          }
        }
      } catch (err) {
        console.error("Error fetching observation detail", err);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    loadChecklist();
    return () => {
      mounted = false;
    };
  }, [open, checklistId]);

  if (!open) return null;

  const renderHazards = (hazardStr) => {
    try {
      const arr =
        typeof hazardStr === "string" ? JSON.parse(hazardStr) : hazardStr;
      if (Array.isArray(arr)) {
        return (
          <div className="flex flex-wrap gap-2">
            {arr.map((h) => (
              <span
                key={h}
                className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-medium text-orange-700"
              >
                {h}
              </span>
            ))}
          </div>
        );
      }
    } catch (e) {
      return <p className="text-sm text-foreground">{hazardStr}</p>;
    }
    return <p className="text-sm text-foreground">{hazardStr}</p>;
  };

  return (
    <div className="fixed inset-0 z-[201] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex h-[95vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-border bg-background shadow-2xl">
        {/* HEADER */}
        <div className="flex items-start justify-between border-b border-border px-6 py-5 bg-white">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                {/* Read Only View */}
              </span>
              {detail?.status && (() => {
                const statusUpper = String(detail.status).toUpperCase();
                const isApproved = statusUpper === "APPROVED" || statusUpper === "CLOSED";
                const isRejected = statusUpper === "REJECTED" || statusUpper === "CLOSED_REJECTED";
                const isPending = statusUpper.includes("PENDING");

                let label = detail.status.charAt(0).toUpperCase() + detail.status.slice(1).toLowerCase();
                if (isApproved) {
                  label = "Approved";
                } else if (isRejected) {
                  label = "Rejected";
                } else if (isPending) {
                  label = pendingStatusLabel || (
                    statusUpper.includes("MAKER") ? "Pending for Rectification" : "Pending for Review"
                  );
                }

                const colorClasses = isApproved
                  ? "bg-green-100 text-green-700"
                  : isRejected
                    ? "bg-red-100 text-red-700"
                    : isPending
                      ? statusUpper.includes("MAKER")
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-orange-100 text-orange-700"
                      : "bg-slate-100 text-slate-700";

                return (
                  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${colorClasses}`}>
                    {label}
                  </span>
                );
              })()}
              {(() => {
                let rCount = Number(detail?.rejection_count || 0);

                if (!rCount) {
                  let history = [];

                  try {
                    history = Array.isArray(detail?.rejection_history)
                      ? detail.rejection_history
                      : JSON.parse(detail?.rejection_history || "[]");
                  } catch {
                    history = [];
                  }

                  if (Array.isArray(history) && history.length > 0) {
                    rCount = history.filter(
                      (item) =>
                        String(item?.decision || "REJECTED").toUpperCase() ===
                        "REJECTED",
                    ).length;
                  } else {
                    const attempts = Array.isArray(detail?.attempts)
                      ? detail.attempts
                      : [];

                    rCount = attempts.filter(
                      (attempt) =>
                        String(
                          attempt?.checker_decision || "",
                        ).toUpperCase() === "REJECTED",
                    ).length;
                  }
                }
                return rCount > 0 ? (
                  <span className="inline-flex rounded-full bg-red-100 px-3 py-1 text-xs font-bold text-red-700">
                    Rejected: {rCount} times
                  </span>
                ) : null;
              })()}
            </div>
            <h2 className="text-xl font-bold text-foreground">
              {detail?.observation_no
                ? `Safety Observation #${detail.observation_no}`
                : detail?.id
                  ? `Safety Observation #${detail.id}`
                  : "Safety Observation Preview"}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {/* Viewing observation details in read-only mode */}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-xl border border-border bg-background p-2 text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
          {loading ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Loading observation...
            </div>
          ) : detail ? (
            <div className="space-y-6">
              <ObservationInfoCard detail={detail} />
              {/* Q1: Unsafe Act / Condition */}
              <ObservationCard
                idx={1}
                title="What Unsafe Act / Condition Observed"
              >
                <div className="rounded-xl border border-border bg-muted/20 p-3 mb-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    Category
                  </p>
                  <p className="text-sm font-medium text-foreground">
                    {detail.unsafe_act_condition_category || "—"}
                  </p>
                </div>
                <div className="rounded-xl border border-border bg-muted/20 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    Description
                  </p>
                  <p className="text-sm text-foreground">
                    {detail.unsafe_act_condition_description || "—"}
                  </p>
                </div>
              </ObservationCard>
              {/* Q2: Location */}
              <ObservationCard idx={2} title="Location">
                <div className="rounded-xl border border-border bg-muted/20 p-3 text-sm text-foreground">
                  {detail.location_combined || "—"}
                </div>
              </ObservationCard>
              {/* Q3: Photograph of Unsafe Act */}
              <ObservationCard
                idx={3}
                title="Photograph of Unsafe Act / Condition"
              >
                {Array.isArray(detail.initial_photographs) && detail.initial_photographs.length > 0 ? (
                  <div className="mt-2 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {detail.initial_photographs.map((photoItem, pIdx) => {
                        const rawUrl =
                          photoItem?.annotated_image_url ||
                          photoItem?.annotated_image ||
                          photoItem?.image_url ||
                          photoItem?.image ||
                          photoItem;
                        const pUrl = resolveMediaUrl(rawUrl);
                        return (
                          <div key={photoItem?.id || pIdx} className="space-y-2 rounded-xl border border-border bg-muted/10 p-3">
                            <div className="relative overflow-hidden rounded-lg border border-border bg-slate-50">
                              {pUrl ? (
                                <img
                                  src={pUrl}
                                  alt={`Unsafe Act ${pIdx + 1}`}
                                  className="h-56 w-full object-contain cursor-pointer"
                                  onClick={() => window.open(pUrl, "_blank")}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "";
                                    e.target.alt = "Image unavailable";
                                  }}
                                />
                              ) : (
                                <div className="flex h-36 items-center justify-center text-xs italic text-muted-foreground">
                                  Image unavailable
                                </div>
                              )}
                              <span className="absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-bold text-white">
                                #{pIdx + 1}
                              </span>
                            </div>
                            {photoItem?.comment && (
                              <div className="rounded-md border border-border/60 bg-muted/30 p-2 text-xs text-foreground">
                                <span className="font-semibold text-muted-foreground uppercase tracking-wide">
                                  Comment:
                                </span>{" "}
                                {photoItem.comment}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : detail.photograph_of_unsafe_act ? (
                  <div className="mt-2">
                    <img
                      src={resolveMediaUrl(detail.photograph_of_unsafe_act)}
                      alt="Unsafe Act"
                      className="h-64 w-full md:w-2/3 rounded-xl border border-border object-contain bg-slate-50"
                      onError={(e) => {
                        console.error(
                          "Observation photo failed to load:",
                          detail.photograph_of_unsafe_act,
                          "resolved:",
                          e.target.src,
                        );
                        e.target.onerror = null;
                        e.target.src = "";
                        e.target.alt = "Image unavailable";
                      }}
                    />
                    {detail.photograph_of_unsafe_act_comment && (
                      <div className="mt-3 rounded-xl border border-border bg-muted/30 p-3 text-sm text-foreground">
                        <span className="font-semibold">Comment:</span>{" "}
                        {detail.photograph_of_unsafe_act_comment}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm italic text-muted-foreground">
                    No photograph provided
                  </p>
                )}
              </ObservationCard>
              {/* Q4: Hazard / Risk */}
              <ObservationCard idx={4} title="Hazard/Risk">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Hazards
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowRiskMatrix(true)}
                      className="text-xs font-bold bg-orange-50 text-orange-600 hover:bg-orange-100 border border-orange-200 px-3 py-1.5 rounded-lg shadow-sm transition-colors"
                    >
                      View Risk Matrix
                    </button>
                  </div>
                  {renderHazards(detail.hazard_categories)}
                </div>
                {detail.risk && (
                  <div
                    className={`rounded-xl border p-3 ${
                      detail.risk === "Low Risk"
                        ? "border-green-200 bg-green-50 text-green-800"
                        : detail.risk === "Medium Risk"
                          ? "border-yellow-200 bg-yellow-50 text-yellow-800"
                          : detail.risk === "High Risk"
                            ? "border-red-200 bg-red-50 text-red-800"
                            : "border-border bg-muted/20 text-foreground"
                    }`}
                  >
                    <p
                      className={`text-xs font-semibold uppercase tracking-wide mb-1 ${
                        detail.risk === "Low Risk"
                          ? "text-green-600"
                          : detail.risk === "Medium Risk"
                            ? "text-yellow-600"
                            : detail.risk === "High Risk"
                              ? "text-red-600"
                              : "text-muted-foreground"
                      }`}
                    >
                      Risk Details
                    </p>
                    <p className="text-sm font-medium">{detail.risk}</p>
                  </div>
                )}
              </ObservationCard>
              {/* Q5: Name of Contractor */}
              <ObservationCard idx={5} title="Name of Contractor">
                <div className="rounded-xl border border-border bg-muted/20 p-3 text-sm text-foreground">
                  {detail.name_of_contractor || detail.contractor_name || "—"}
                </div>
              </ObservationCard>
              {/* Q6: Target Date */}
              <ObservationCard idx={6} title="Target Date">
                <div
                  className={`rounded-xl border p-3 text-sm font-medium ${
                    detail.risk === "Low Risk"
                      ? "border-green-200 bg-green-50 text-green-800"
                      : detail.risk === "Medium Risk"
                        ? "border-yellow-200 bg-yellow-50 text-yellow-800"
                        : detail.risk === "High Risk"
                          ? "border-red-200 bg-red-50 text-red-800"
                          : "border-border bg-muted/20 text-foreground"
                  }`}
                >
                  {detail.target_date
                    ? formatDateTime(detail.target_date)
                    : "—"}
                </div>
              </ObservationCard>
              {/* Q7: CA/PA To Be Taken */}
              <ObservationCard idx={7} title="CA/PA To Be Taken">
                {detail.ca_pa_combined ? (
                  <div className="rounded-xl border border-border bg-muted/20 p-3 text-sm text-foreground">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: detail.ca_pa_combined,
                      }}
                      className="whitespace-pre-wrap"
                    />
                  </div>
                ) : (
                  <p className="text-sm italic text-amber-600 font-medium bg-amber-50 px-3 py-2 rounded-lg border border-amber-200 inline-block">
                    Pending rectification
                  </p>
                )}
              </ObservationCard>
              {/* Q8: Closer Photograph (Conversation UI) */}
              {(() => {
                const buildObservationThread = () => {
                  const thread = [];

                  const attempts = Array.isArray(detail?.attempts)
                    ? [...detail.attempts].sort(
                        (a, b) =>
                          Number(a?.attempt_no || 0) -
                          Number(b?.attempt_no || 0),
                      )
                    : [];

                  const rejectionHistory = parseRejectionHistory(
                    detail?.rejection_history,
                  );

                  const photoPath = (photo) => {
                    if (!photo) return "";
                    if (typeof photo === "string") return photo;
                    return (
                      photo?.annotated_image_url ||
                      photo?.annotated_image ||
                      photo?.image_url ||
                      photo?.image ||
                      ""
                    );
                  };

                  attempts.forEach((attempt) => {
                    const attemptNo = Number(
                      attempt?.attempt_no || 1,
                    );

                    const makerName =
                      attempt?.claimed_by_safety_officer_name ||
                      detail?.maker_name ||
                      "Maker";

                    const makerPhotos = Array.isArray(
                      attempt?.safety_officer_photographs,
                    )
                      ? attempt.safety_officer_photographs
                      : [];

                    const makerSubmittedAt =
                      attempt?.safety_officer_submitted_at ||
                      makerPhotos[0]?.uploaded_at ||
                      "";

                    // ── FIX: single Maker message per submission, ──
                    // ── carrying all photos + one deduped comment ──
                    if (
                      attempt?.safety_officer_submitted_at ||
                      attempt?.safety_officer_comment ||
                      makerPhotos.length > 0
                    ) {
                      const submissionComment =
                        attempt?.safety_officer_comment ||
                        makerPhotos.find((p) => p?.comment)?.comment ||
                        (makerPhotos.length === 0
                          ? "Submitted for rectification"
                          : "");

                      const makerPhotoUrls = makerPhotos
                        .map(photoPath)
                        .filter(Boolean);

                      thread.push({
                        role: "maker",
                        attemptNo,
                        actorName: makerName,
                        action:
                          attemptNo === 1
                            ? "SUBMITTED"
                            : "REWORK_SUBMITTED",
                        answer: "",
                        comment: submissionComment,
                        photoUrl: makerPhotoUrls[0] || "",
                        photoUrls: makerPhotoUrls,
                        isFinalImage: makerPhotos.some(
                          (p) => p?.is_final_image === true,
                        ),
                        // Maker signature removed as it is already shown in the Sign Off section View button below
                        signatureUrl: "",
                        date: makerSubmittedAt,
                      });
                    }

                    const reviewEvents = buildAttemptReviewEvents(
                      attempt,
                      rejectionHistory,
                      detail,
                    );

                    reviewEvents.forEach((evt) => {
                      const rejectPhotoUrls = (evt.rejectionPhotos || [])
                        .map(photoPath)
                        .filter(Boolean);

                      thread.push({
                        role:
                          evt.role === "CONTRACTOR_CHECKER"
                            ? "contractor_checker"
                            : "checker",
                        attemptNo,
                        actorName: evt.reviewerName,
                        action: evt.rawDecision,
                        answer: evt.reviewerRole,
                        comment:
                          evt.remarks ||
                          (evt.rawDecision === "APPROVED"
                            ? (evt.role === "CONTRACTOR_CHECKER"
                                ? "Approved and forwarded to ADL EHS"
                                : "Observation rectification approved.")
                            : "Observation rejected."),
                        photoUrl: rejectPhotoUrls[0] || "",
                        photoUrls: rejectPhotoUrls,
                        photoLabel:
                          rejectPhotoUrls.length > 0
                            ? "Checker Attached Photo"
                            : "",
                        signatureUrl:
                          evt.rawDecision === "APPROVED"
                            ? ""
                            : evt.signature || "",
                        date: evt.reviewedAt || "",
                      });
                    });
                  });

                  thread.sort(
                    (a, b) =>
                      new Date(a?.date || 0).getTime() -
                      new Date(b?.date || 0).getTime(),
                  );

                  return thread;
                };

                const thread = buildObservationThread();

                return (
                  <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm mt-6">
                    <div className="flex w-full items-start gap-3 px-5 py-4 text-left border-b border-border/50 bg-muted/10">
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-xs font-bold text-white shadow-sm">
                        8
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold leading-snug text-foreground sm:text-base">
                          Closer Photograph
                        </p>
                      </div>
                    </div>
                    <div className="p-5">
                      {thread.length === 0 ? (
                        <div className="flex h-32 items-center justify-center rounded-xl border border-dashed border-amber-300 bg-amber-50 text-sm font-medium text-amber-700">
                          Pending rectification
                        </div>
                      ) : (
                        <div className="space-y-5">
                          {thread.map((entry, i) => (
                            <ThreadEntry
                              key={`${entry.role}-${i}`}
                              entry={entry}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              {/* Checker remarks are now fully integrated into the conversation thread above */}{" "}
            </div>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              No observation data found.
            </div>
          )}

          {detail && (
            <ObservationSignOffSection
              detail={detail}
              onViewSignature={setSignatureUser}
            />
          )}
        </div>
      </div>

      <SignatureViewModal
        open={!!signatureUser}
        user={signatureUser}
        onClose={() => setSignatureUser(null)}
      />
      <RiskMatrixModal
        open={showRiskMatrix}
        onClose={() => setShowRiskMatrix(false)}
      />
    </div>
  );
}